import { useEffect, useRef, useState } from 'react';

const HEADLINE_STAGES = [
  { firstLine: 'WE BUILD', secondLine: 'DIGITAL' },
  { firstLine: 'SCALE YOUR', secondLine: 'BUSINESS' },
  { firstLine: 'CODE YOUR', secondLine: 'FUTURE' },
];

interface AppScreen {
  bg: string;
  accent: string;
  title: string;
  subtitle: string;
  items: { icon: string; label: string; value: string; color: string }[];
  badge: string;
}

const APP_SCREENS: AppScreen[] = [
  {
    bg: 'linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    accent: '#FFD93D',
    title: 'Analytics Pro',
    subtitle: 'Revenue Overview',
    items: [
      { icon: 'ri-bar-chart-2-line', label: 'Total Revenue', value: '$128,450', color: '#FFD93D' },
      { icon: 'ri-user-line', label: 'Active Users', value: '24,891', color: '#34d399' },
      { icon: 'ri-arrow-up-line', label: 'Growth', value: '+18.4%', color: '#60a5fa' },
    ],
    badge: 'DASHBOARD',
  },
  {
    bg: 'linear-gradient(160deg, #1a0a00 0%, #2d1500 50%, #1a0a00 100%)',
    accent: '#FF8C42',
    title: 'Commerce Hub',
    subtitle: 'Store Performance',
    items: [
      { icon: 'ri-shopping-bag-line', label: 'Orders Today', value: '1,247', color: '#FF8C42' },
      { icon: 'ri-star-line', label: 'Rating', value: '4.9 / 5.0', color: '#FFD93D' },
      { icon: 'ri-truck-line', label: 'Delivered', value: '98.6%', color: '#34d399' },
    ],
    badge: 'E-COMMERCE',
  },
  {
    bg: 'linear-gradient(160deg, #0a1a0a 0%, #0d2b0d 50%, #0a1a0a 100%)',
    accent: '#C4F547',
    title: 'Campaign AI',
    subtitle: 'Marketing Pulse',
    items: [
      { icon: 'ri-megaphone-line', label: 'Reach', value: '2.4M', color: '#C4F547' },
      { icon: 'ri-cursor-line', label: 'CTR', value: '5.82%', color: '#FFD93D' },
      { icon: 'ri-funds-line', label: 'ROAS', value: '6.4x', color: '#fb923c' },
    ],
    badge: 'MARKETING',
  },
];

function getStage(progress: number) {
  if (progress >= 0.66) return 2;
  if (progress >= 0.33) return 1;
  return 0;
}

function PhoneScreen({ screen, visible }: { screen: AppScreen; visible: boolean }) {
  return (
    <div
      className="absolute inset-0 transition-opacity duration-700"
      style={{ opacity: visible ? 1 : 0 }}
    >
      <div className="w-full h-full flex flex-col" style={{ background: screen.bg }}>
        {/* Status bar */}
        <div className="flex items-center justify-between px-5 pt-4 pb-1">
          <span className="text-white/70 text-[10px] font-bold">14:07</span>
          <div className="flex items-center gap-1">
            <div className="flex gap-[2px] items-end h-3">
              {[2, 3, 4, 5].map((h, i) => (
                <div key={i} className="w-[3px] rounded-sm bg-white/80" style={{ height: `${h * 2}px` }} />
              ))}
            </div>
            <div className="w-4 h-3 flex items-center justify-center">
              <i className="ri-wifi-line text-white/80" style={{ fontSize: '11px' }} />
            </div>
          </div>
        </div>

        {/* App header */}
        <div className="px-5 pt-3 pb-4">
          <div
            className="font-black uppercase text-xs tracking-widest mb-1"
            style={{ color: screen.accent, fontFamily: "'Bebas Neue', sans-serif", fontSize: '11px', letterSpacing: '0.3em' }}
          >
            {screen.badge}
          </div>
          <div className="text-white font-bold text-base">{screen.title}</div>
          <div className="text-white/50 text-[11px] font-semibold">{screen.subtitle}</div>
        </div>

        {/* Chart area */}
        <div className="mx-4 mb-4 rounded-2xl p-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="flex items-end gap-1 h-20">
            {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 100].map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm"
                style={{
                  height: `${h}%`,
                  background: i === 11 ? screen.accent : `${screen.accent}40`,
                  transition: 'height 0.5s ease',
                }}
              />
            ))}
          </div>
          <div className="flex justify-between mt-2">
            {['Jan', 'Apr', 'Jul', 'Oct', 'Dec'].map((m) => (
              <span key={m} className="text-white/30 text-[9px] font-semibold">{m}</span>
            ))}
          </div>
        </div>

        {/* Metrics */}
        <div className="px-4 flex flex-col gap-2 flex-1">
          {screen.items.map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-between rounded-xl px-3 py-2"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{ background: `${item.color}20` }}
                >
                  <i className={`${item.icon}`} style={{ color: item.color, fontSize: '13px' }} />
                </div>
                <span className="text-white/60 text-[11px] font-semibold">{item.label}</span>
              </div>
              <span className="font-black text-[13px]" style={{ color: item.color, fontFamily: "'Bebas Neue', sans-serif" }}>
                {item.value}
              </span>
            </div>
          ))}
        </div>

        {/* CTA bar */}
        <div className="px-4 pb-5 pt-3">
          <div
            className="w-full rounded-2xl py-3 text-center font-black text-sm text-black"
            style={{ background: screen.accent, fontFamily: "'Bebas Neue', sans-serif", fontSize: '15px' }}
          >
            VIEW FULL REPORT
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isHeroActive, setIsHeroActive] = useState(true);
  const [stage, setStage] = useState(0);
  const [screenProgress, setScreenProgress] = useState(0);
  const heroActiveRef = useRef(true);
  const stageRef = useRef(0);
  // Animation occupies 3 full screens (one per app screen), navbar color only changes after all 3
  const SCROLL_SCREENS = 5;
  const ANIM_SCREENS = 3; // screens dedicated purely to phone animation

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const syncFromScroll = () => {
      const sectionTop = section.offsetTop;
      const animRange = ANIM_SCREENS * window.innerHeight;
      const totalRange = SCROLL_SCREENS * window.innerHeight;

      // Phone animation progress — starts immediately at first scroll pixel
      const scrolledInAnim = window.scrollY - sectionTop;
      const animProgress = Math.min(Math.max(scrolledInAnim / animRange, 0), 1);

      // Hero stays "active" (fixed) until we've scrolled past the full section
      const sectionEnd = sectionTop + totalRange;
      const nextHeroActive = window.scrollY < sectionEnd;
      const nextStage = getStage(animProgress);

      if (heroActiveRef.current !== nextHeroActive) {
        heroActiveRef.current = nextHeroActive;
        setIsHeroActive(nextHeroActive);
      }
      if (stageRef.current !== nextStage) {
        stageRef.current = nextStage;
        setStage(nextStage);
      }
      setScreenProgress(animProgress);
    };

    window.addEventListener('scroll', syncFromScroll, { passive: true });
    window.addEventListener('resize', syncFromScroll);
    syncFromScroll();

    return () => {
      window.removeEventListener('scroll', syncFromScroll);
      window.removeEventListener('resize', syncFromScroll);
    };
  }, []);

  const headline = HEADLINE_STAGES[stage];
  const tilt = (screenProgress * 18 - 9).toFixed(1);
  const translateY = Math.sin(screenProgress * Math.PI * 3) * 16;

  return (
    <section
      id="hero-section"
      ref={sectionRef}
      className="relative w-full"
      style={{ height: `${(SCROLL_SCREENS + 1) * 100}vh` }}
    >
      <div
        className={`${isHeroActive ? 'fixed inset-0 z-20' : 'absolute inset-x-0 bottom-0 z-0'} h-screen w-full overflow-hidden`}
        style={{ background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 40%, #2a1f00 100%)' }}
      >
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(#FFD93D 1px, transparent 1px), linear-gradient(90deg, #FFD93D 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        {/* Glow orbs */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #FFD93D 0%, transparent 70%)' }} />
        <div className="absolute bottom-1/3 left-1/5 w-64 h-64 rounded-full opacity-8"
          style={{ background: 'radial-gradient(circle, #FF8C42 0%, transparent 70%)' }} />

        {/* Content layout */}
        <div className="absolute top-20 inset-x-0 bottom-0 flex items-center px-8 md:px-16">
          <div className="w-full flex flex-row items-center gap-8">
            {/* Left: Text */}
            <div className="flex-1 max-w-2xl">
              <p
                className="text-[#FFD93D] font-bold uppercase tracking-[0.4em] text-sm md:text-base mb-5"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Regent Digital LTD · UK Registered
              </p>
              <h1
                className="text-white font-black uppercase leading-none mb-6 transition-all duration-500"
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 'clamp(40px, 10vw, 140px)',
                  lineHeight: 0.9,
                }}
              >
                {headline.firstLine}
                <br />
                <span className="text-[#FFD93D]">{headline.secondLine}</span>
              </h1>
              <p
                className="text-white/60 font-normal text-base md:text-xl leading-relaxed max-w-lg"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Custom software development, IT consulting, and digital marketing — all under one roof. We turn your ideas into scalable products.
              </p>

              <div className="flex gap-4 mt-10">
                <button
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-[#FFD93D] text-black font-black uppercase px-8 py-2 md:py-4 rounded-full hover:bg-[#FF8C42] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px' }}
                >
                  GET IN TOUCH
                </button>


                <button
                  onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-2 border-white/30 text-white font-black uppercase px-8 py-2 md:py-4 rounded-full hover:border-[#FFD93D] hover:text-[#FFD93D] transition-colors cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px' }}
                >
                  OUR SERVICES
                </button>
              </div>

              {/* Stats strip */}
              <div className="flex flex-wrap gap-8 mt-12">
                {[
                  { val: '120+', label: 'Projects' },
                  { val: '10+', label: 'Years' },
                  { val: '80+', label: 'Clients' },
                ].map((s) => (
                  <div key={s.label}>
                    <div
                      className="text-[#FFD93D] font-black leading-none"
                      style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px' }}
                    >
                      {s.val}
                    </div>
                    <div className="text-white/50 font-semibold text-sm uppercase tracking-widest">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: iPhone mockup */}
            <div className="flex-shrink-0  hidden md:flex items-center justify-center relative w-[280px] lg:ml-8 xl:ml-16">
              {/* Glow behind phone */}
              <div
                className="absolute inset-0 rounded-full opacity-25 blur-3xl"
                style={{ background: '#FFD93D', transform: 'scale(0.8)' }}
              />

              <div
                className="relative transition-transform duration-700 ease-out"
                style={{
                  transform: `rotateY(${tilt}deg) translateY(${translateY}px)`,
                  transformStyle: 'preserve-3d',
                  perspective: '1000px',
                }}
              >
                {/* Phone outer shell */}
                <div
                  className="relative"
                  style={{
                    width: '264px',
                    height: '552px',
                    borderRadius: '46px',
                    background: 'linear-gradient(145deg, #3a3a3a, #1a1a1a)',
                    boxShadow: '0 0 0 1px #404040, 0 0 0 3px #252525, 0 40px 80px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.12)',
                    padding: '10px',
                  }}
                >
                  {/* Screen area */}
                  <div
                    className="relative overflow-hidden"
                    style={{ borderRadius: '38px', height: '100%', background: '#000' }}
                  >
                    {/* App screens */}
                    {APP_SCREENS.map((screen, i) => (
                      <PhoneScreen key={i} screen={screen} visible={stage === i} />
                    ))}

                    {/* Dynamic island */}
                    <div
                      className="absolute top-3 left-1/2 z-20 bg-black"
                      style={{
                        transform: 'translateX(-50%)',
                        width: '90px',
                        height: '28px',
                        borderRadius: '20px',
                        boxShadow: '0 0 0 1px rgba(255,255,255,0.1)',
                      }}
                    />

                    {/* Bottom home bar */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 bg-white/25 rounded-full z-20" />

                    {/* Screen indicator dots */}
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
                      {APP_SCREENS.map((_, i) => (
                        <div
                          key={i}
                          className="rounded-full transition-all duration-300"
                          style={{
                            width: stage === i ? '16px' : '4px',
                            height: '4px',
                            background: stage === i ? '#FFD93D' : 'rgba(255,255,255,0.3)',
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Side buttons */}
                  <div className="absolute right-[-3px] top-28 w-[3px] h-14 bg-[#404040] rounded-l-sm" />
                  <div className="absolute left-[-3px] top-24 w-[3px] h-8 bg-[#404040] rounded-r-sm" />
                  <div className="absolute left-[-3px] top-36 w-[3px] h-12 bg-[#404040] rounded-r-sm" />
                  <div className="absolute left-[-3px] top-52 w-[3px] h-12 bg-[#404040] rounded-r-sm" />
                </div>

                {/* Floating badges */}
                <div
                  className="absolute -right-16 top-10 bg-[#FFD93D] text-black font-black text-xs px-3 py-2 rounded-xl whitespace-nowrap"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '14px' }}
                >
                  <i className="ri-code-s-slash-line mr-1" />
                  CUSTOM BUILT
                </div>
                <div
                  className="absolute -left-16 bottom-14 bg-[#FF8C42] text-white font-black text-xs px-3 py-2 rounded-xl whitespace-nowrap"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '14px' }}
                >
                  <i className="ri-rocket-line mr-1" />
                  FAST DELIVERY
                </div>
                <div
                  className="absolute -right-14 bottom-36 bg-[#C4F547] text-black font-black text-xs px-3 py-2 rounded-xl whitespace-nowrap"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '13px' }}
                >
                  <i className="ri-shield-check-line mr-1" />
                  SECURE
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50">
          <span className="text-white text-xs uppercase tracking-widest font-semibold">Scroll</span>
          <div className="w-[1px] h-8 bg-white/50 animate-pulse" />
        </div>
      </div>
    </section>
  );
}
